import { createTheme, ThemeOptions } from '@mui/material/styles';

// Color palette based on UniiClub design
const colors = {
  primary: {
    main: '#667eea',      // Unii Club purple-blue
    light: '#8799f3',     // Light purple-blue
    dark: '#4c63b6',      // Darker purple-blue
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#764ba2',      // Unii Club purple
    light: '#9f6ec7',     // Light purple
    dark: '#5a3a7a',      // Darker purple
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F8F9FA',   // Light neutral background
    paper: '#FFFFFF',     // White cards/surfaces
  },
  text: {
    primary: '#1C1C1E',   // Primary text
    secondary: '#8E8E93', // Secondary text
  },
  success: {
    main: '#34C759',      // iOS green
    light: '#E8F5E8',
    dark: '#248A3D',
  },
  warning: {
    main: '#FF9500',      // iOS orange
    light: '#FFF4E6',
    dark: '#CC7700',
  },
  error: {
    main: '#FF3B30',      // iOS red
    light: '#FFEBEE',
    dark: '#D32F2F',
  },
  info: {
    main: '#007AFF',      // Same as primary
    light: '#E3F2FD',
    dark: '#0056CC',
  },
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// Role-specific colors
export const roleColors = {
  Student: {
    background: '#E3F2FD',  // Light blue
    text: '#1976D2',        // Blue
  },
  Faculty: {
    background: '#FFF3E0',  // Light orange
    text: '#F57C00',        // Orange
  },
  Ambassador: {
    background: '#F3E5F5',  // Light purple
    text: '#7B1FA2',        // Purple
  },
};

// Priority colors for activities
export const priorityColors = {
  low: {
    background: '#E8F5E8',
    text: '#2E7D32',
    border: '#4CAF50',
  },
  medium: {
    background: '#FFF3E0',
    text: '#F57C00',
    border: '#FF9800',
  },
  high: {
    background: '#FFEBEE',
    text: '#D32F2F',
    border: '#F44336',
  },
};

// Activity type colors
export const activityTypeColors = {
  announcement: {
    background: '#E3F2FD',
    text: '#1976D2',
    icon: '📢',
  },
  activity: {
    background: '#E8F5E8',
    text: '#2E7D32',
    icon: '🎯',
  },
  requirement: {
    background: '#FFEBEE',
    text: '#D32F2F',
    icon: '⚠️',
  },
  event: {
    background: '#FFF3E0',
    text: '#F57C00',
    icon: '📅',
  },
  discussion: {
    background: '#F3E5F5',
    text: '#7B1FA2',
    icon: '💬',
  },
};

// Typography
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
};

// Component overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none' as const,
        fontWeight: 600,
        padding: '10px 20px',
        transition: 'all 0.3s ease',
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
        '&:hover': {
          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
      },
      outlined: {
        borderWidth: 2,
        '&:hover': {
          borderWidth: 2,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        height: 28,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: '1rem',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1C1C1E',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        border: 'none',
        boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        margin: '2px 8px',
        '&.Mui-selected': {
          backgroundColor: colors.primary.light,
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.light,
          },
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 600,
        fontSize: '0.875rem',
      },
    },
  },
  MuiBottomNavigation: {
    styleOverrides: {
      root: {
        boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.1)',
        border: 'none',
      },
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          color: colors.primary.main,
        },
      },
    },
  },
};

// Create theme
const themeOptions: ThemeOptions = {
  palette: colors,
  typography,
  components,
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
};

export const theme = createTheme(themeOptions);

// Custom breakpoints for responsive design
export const breakpoints = {
  mobile: '@media (max-width: 599px)',
  tablet: '@media (min-width: 600px) and (max-width: 899px)',
  desktop: '@media (min-width: 900px)',
  large: '@media (min-width: 1200px)',
};

// Z-index values
export const zIndex = {
  drawer: 1000,
  appBar: 1100,
  modal: 1200,
  snackbar: 1300,
  tooltip: 1400,
};

// Animation durations
export const transitions = {
  short: '0.2s',
  standard: '0.3s',
  long: '0.5s',
};

// Common shadows
export const shadows = {
  card: '0 1px 3px rgba(0, 0, 0, 0.1)',
  raised: '0 2px 8px rgba(0, 0, 0, 0.15)',
  floating: '0 4px 16px rgba(0, 0, 0, 0.2)',
};

export default theme;